/**
 * @boli/core - Wallet Service
 *
 * Platform-agnostic wallet logic for Boli Live "Bidding Passport" system.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - Balance management (deposits via Easypaisa/JazzCash)
 * - Lock/unlock for auction participation
 * - Automatic unlock on losing auction
 * - Payment deduction on winning
 *
 * Flow:
 * 1. User loads wallet via Easypaisa/JazzCash/Bank
 * 2. User enters auction → 10% of maxBidAmount is locked
 * 3. User bids (only up to maxBidAmount)
 * 4. Auction ends:
 *    - Winner: Lock converted to payment
 *    - Losers: Lock released instantly
 */

import type {
  Wallet,
  WalletTransaction,
  BidLock,
  WalletBalanceResponse,
  DepositRequest,
  EnterAuctionRequest,
  EnterAuctionResponse,
  LeaveAuctionRequest,
  ApiResponse,
  WALLET_CONFIG,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IWalletService {
  /**
   * Get user's wallet balance and active locks
   */
  getBalance(): Promise<WalletBalanceResponse | null>;

  /**
   * Initialize deposit (returns payment URL for Easypaisa/JazzCash)
   */
  initializeDeposit(request: DepositRequest): Promise<ApiResponse<{ paymentUrl: string; transactionId: string }>>;

  /**
   * Enter an auction by locking balance
   */
  enterAuction(request: EnterAuctionRequest): Promise<EnterAuctionResponse>;

  /**
   * Leave an auction and release lock
   */
  leaveAuction(request: LeaveAuctionRequest): Promise<ApiResponse<void>>;

  /**
   * Get transaction history
   */
  getTransactions(limit?: number): Promise<WalletTransaction[]>;

  /**
   * Get active bid locks
   */
  getActiveLocks(): Promise<BidLock[]>;

  /**
   * Check if user can bid on auction (has active lock)
   */
  canBidOnAuction(auctionId: string, bidAmount: number): Promise<{ canBid: boolean; reason?: string; maxBid?: number }>;

  /**
   * Calculate required lock amount for a max bid
   */
  calculateLockAmount(maxBidAmount: number): number;
}

export interface WalletServiceConfig {
  apiBaseUrl: string;
  lockPercentage: number;
  minLockAmount: number;
  minDeposit: number;
  maxDeposit: number;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_WALLET_CONFIG: WalletServiceConfig = {
  apiBaseUrl: '',
  lockPercentage: 0.10, // 10%
  minLockAmount: 500, // PKR
  minDeposit: 100, // PKR
  maxDeposit: 500000, // PKR
};

// ============================================================================
// WALLET SERVICE IMPLEMENTATION
// ============================================================================

export class WalletService implements IWalletService {
  private config: WalletServiceConfig;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  private getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<WalletServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_WALLET_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Get authorization headers
   */
  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get user's wallet balance and active locks
   */
  async getBalance(): Promise<WalletBalanceResponse | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/wallet/balance`,
        { headers: await this.getHeaders() }
      );

      if (response.status === 404) {
        // No wallet yet, return null
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch wallet balance');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get wallet balance:', error);
      return null;
    }
  }

  /**
   * Initialize deposit (returns payment URL for Easypaisa/JazzCash)
   */
  async initializeDeposit(
    request: DepositRequest
  ): Promise<ApiResponse<{ paymentUrl: string; transactionId: string }>> {
    try {
      // Validate amount
      if (request.amount < this.config.minDeposit) {
        return {
          success: false,
          error: {
            code: 'AMOUNT_TOO_LOW',
            message: `Minimum deposit is PKR ${this.config.minDeposit}`,
          },
        };
      }

      if (request.amount > this.config.maxDeposit) {
        return {
          success: false,
          error: {
            code: 'AMOUNT_TOO_HIGH',
            message: `Maximum deposit is PKR ${this.config.maxDeposit}`,
          },
        };
      }

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/wallet/deposit`,
        {
          method: 'POST',
          headers: await this.getHeaders(),
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'DEPOSIT_FAILED',
            message: errorData.message || 'Failed to initialize deposit',
          },
        };
      }

      return { success: true, data: await response.json() };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Enter an auction by locking balance
   */
  async enterAuction(request: EnterAuctionRequest): Promise<EnterAuctionResponse> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/wallet/enter-auction`,
        {
          method: 'POST',
          headers: await this.getHeaders(),
          body: JSON.stringify(request),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: data.code || 'ENTER_FAILED',
            message: data.message || 'Failed to enter auction',
            requiredAmount: data.requiredAmount,
            currentBalance: data.currentBalance,
          },
        };
      }

      return {
        success: true,
        bidLock: data.bidLock,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'INSUFFICIENT_BALANCE',
          message: error instanceof Error ? error.message : 'Failed to enter auction',
        },
      };
    }
  }

  /**
   * Leave an auction and release lock
   */
  async leaveAuction(request: LeaveAuctionRequest): Promise<ApiResponse<void>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/wallet/leave-auction`,
        {
          method: 'POST',
          headers: await this.getHeaders(),
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'LEAVE_FAILED',
            message: errorData.message || 'Failed to leave auction',
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get transaction history
   */
  async getTransactions(limit: number = 50): Promise<WalletTransaction[]> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/wallet/transactions?limit=${limit}`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }

      const data = await response.json();
      return data.transactions || [];
    } catch (error) {
      console.error('Failed to get transactions:', error);
      return [];
    }
  }

  /**
   * Get active bid locks
   */
  async getActiveLocks(): Promise<BidLock[]> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/wallet/locks`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch locks');
      }

      const data = await response.json();
      return data.locks || [];
    } catch (error) {
      console.error('Failed to get active locks:', error);
      return [];
    }
  }

  /**
   * Check if user can bid on auction (has active lock)
   */
  async canBidOnAuction(
    auctionId: string,
    bidAmount: number
  ): Promise<{ canBid: boolean; reason?: string; maxBid?: number }> {
    try {
      const locks = await this.getActiveLocks();
      const lock = locks.find((l) => l.auctionId === auctionId && l.status === 'active');

      if (!lock) {
        return {
          canBid: false,
          reason: 'You must enter this auction first. Please lock a balance to participate.',
        };
      }

      if (bidAmount > lock.maxBidAmount) {
        return {
          canBid: false,
          reason: `Your maximum bid for this auction is PKR ${lock.maxBidAmount.toLocaleString()}. Increase your lock to bid higher.`,
          maxBid: lock.maxBidAmount,
        };
      }

      return {
        canBid: true,
        maxBid: lock.maxBidAmount,
      };
    } catch (error) {
      return {
        canBid: false,
        reason: 'Unable to verify bidding eligibility. Please try again.',
      };
    }
  }

  /**
   * Calculate required lock amount for a max bid
   * Uses 10% of maxBidAmount or minimum 500 PKR, whichever is higher
   */
  calculateLockAmount(maxBidAmount: number): number {
    const percentageLock = Math.floor(maxBidAmount * this.config.lockPercentage);
    return Math.max(percentageLock, this.config.minLockAmount);
  }

  /**
   * Calculate available balance (total - locked)
   */
  calculateAvailableBalance(wallet: Wallet): number {
    return wallet.balance - wallet.lockedBalance;
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
}

// ============================================================================
// WALLET SERVICE FACTORY
// ============================================================================

let walletServiceInstance: WalletService | null = null;

export function createWalletService(
  config: Partial<WalletServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): WalletService {
  walletServiceInstance = new WalletService(config, fetchAdapter, getAuthToken);
  return walletServiceInstance;
}

export function getWalletService(): WalletService {
  if (!walletServiceInstance) {
    throw new Error('WalletService not initialized. Call createWalletService first.');
  }
  return walletServiceInstance;
}
